import { BlogPost } from "./types";

/**
 * Daily posts: add a markdown file at src/content/blog/posts/YYYY-MM-DD-slug.md
 * with frontmatter (title, slug, date, excerpt). New files are picked up
 * automatically. No other files need to change.
 */
const postFiles = import.meta.glob("./posts/*.md", {
 query: "?raw",
 import: "default",
 eager: true,
}) as Record<string, string>;

function parseFrontmatter(raw: string): BlogPost {
 const match = raw.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
 if (!match) {
 throw new Error("Blog post is missing YAML frontmatter");
 }

 const [, frontmatter, content] = match;
 const fields: Record<string, string> = {};

 for (const line of frontmatter.split("\n")) {
 const separator = line.indexOf(":");
 if (separator === -1) continue;
 const key = line.slice(0, separator).trim();
 let value = line.slice(separator + 1).trim();
 if (
 (value.startsWith('"') && value.endsWith('"')) ||
 (value.startsWith("'") && value.endsWith("'"))
 ) {
 value = value.slice(1, -1).replace(/\\"/g, '"').replace(/\\'/g, "'");
 }
 fields[key] = value;
 }

 return {
 slug: fields.slug,
 title: fields.title,
 date: fields.date,
 excerpt: fields.excerpt ?? "",
 content: content.trim(),
 };
}

function byDateDesc(a: BlogPost, b: BlogPost) {
 return b.date.localeCompare(a.date);
}

export const blogPosts: BlogPost[] = Object.values(postFiles)
 .map(parseFrontmatter)
 .sort(byDateDesc);

export function getPostBySlug(slug: string | undefined): BlogPost | undefined {
 if (!slug) return undefined;
 return blogPosts.find((post) => post.slug === slug);
}

export type { BlogPost };
